import logger from '@eventbrite/client-logger';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Dispatch } from 'redux';
import { change, setSubmitFailed, stopSubmit, submit } from 'redux-form';
import {
    CHECKOUT_FORM_NAME,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_PAGE_FORM_NAME,
} from '../constants';
import { ContactFields } from '../models/order';
import { PaypalBuyerDetails } from '../models/paymentMethods/paypal';
import { getCurrency } from '../selectors/event';
import {
    getPaypalScriptStatus,
    isExpressPaypalEnabled,
} from '../selectors/paypal';
import { isTicketSelectionFullSizePage } from '../selectors/routing';
import { CheckoutState } from '../state';
import { PaypalScriptStatus } from '../state/paypal';
import { clearNonceInForm } from '../utils/expressCheckout';
import {
    loadPaypalScript,
    PayPalFundingSource,
} from '../utils/paypalScriptLoader';
import { trackExpressCheckoutShowed } from './eventAnalytics';
import { getAndSetBraintreeToken } from './order/common';

export enum PaypalActionType {
    setIsLoadingPaypal = 'setIsLoadingPaypal',
    setScriptStatus = 'setScriptStatus',
}

export interface SetIsLoadingPaypalAction {
    type: PaypalActionType.setIsLoadingPaypal;
    payload: boolean;
}

export interface SetPaypalScriptStatusAction {
    type: PaypalActionType.setScriptStatus;
    payload: {
        scriptStatus: PaypalScriptStatus;
    };
}

export const setIsLoadingPaypal = (
    isLoading: boolean,
): SetIsLoadingPaypalAction => ({
    type: PaypalActionType.setIsLoadingPaypal,
    payload: isLoading,
});

export const setPaypalScriptStatusAction = (
    scriptStatus: PaypalScriptStatus,
): SetPaypalScriptStatusAction => ({
    type: PaypalActionType.setScriptStatus,
    payload: {
        scriptStatus,
    },
});

export type PaypalAction =
    | SetPaypalScriptStatusAction
    | SetIsLoadingPaypalAction;

export const initializePaypalSDKAction =
    ({
        disableFunding,
        enableFunding,
        needsToReload,
    }: {
        disableFunding?: Array<PayPalFundingSource>;
        enableFunding?: Array<PayPalFundingSource>;
        needsToReload?: boolean;
    }) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        try {
            const braintreeClientToken = await dispatch(
                getAndSetBraintreeToken(),
            );

            if (
                !braintreeClientToken ||
                getPaypalScriptStatus(getState()) !== PaypalScriptStatus.init
            ) {
                return;
            }

            dispatch(setPaypalScriptStatusAction(PaypalScriptStatus.loading));

            loadPaypalScript({
                amountCurrency: getCurrency(getState()) || 'USD',
                braintreeClientToken,
                disableFunding,
                enableFunding,
                locale: getState().app.locale,
                onScriptLoad: () => {
                    dispatch(
                        setPaypalScriptStatusAction(PaypalScriptStatus.loaded),
                    );
                },
                onScriptLoadError: () => {
                    dispatch(
                        setPaypalScriptStatusAction(PaypalScriptStatus.failed),
                    );
                },
                needsToReload,
            });
        } catch (error) {
            logger.error(error);
        }
    };

export const initializePaypalIfExpressCheckout =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        if (isExpressPaypalEnabled(getState())) {
            dispatch(
                initializePaypalSDKAction({
                    disableFunding: [PayPalFundingSource.credit],
                }),
            );
            // TODO track payment method and create ticket for GAnalytics
            dispatch(trackExpressCheckoutShowed(TICKETS_FULL_SIZE_PATH));
        }
    };

const getPaypalBuyerData = (
    buyerDetails: Pick<PaypalBuyerDetails, 'firstName' | 'lastName' | 'email'>,
) => ({
    [ContactFields.FIRST_NAME]: buyerDetails.firstName,
    [ContactFields.LAST_NAME]: buyerDetails.lastName,
    [ContactFields.EMAIL]: buyerDetails.email,
});

export const onPaypalPaymentAuthorizeHandler =
    (buyerDetails: PaypalBuyerDetails) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const { nonce, email, credit } = buyerDetails;

        const isTSPage = isTicketSelectionFullSizePage(getState());

        clearNonceInForm(dispatch);

        dispatch(
            change(CHECKOUT_FORM_NAME, 'payment.paypalBraintreeNonce', nonce),
        );

        dispatch(change(CHECKOUT_FORM_NAME, 'payment.paypalEmail', email));

        dispatch(
            change(
                CHECKOUT_FORM_NAME,
                'payment.isPaypalCreditSelected',
                credit,
            ),
        );

        if (isTSPage) {
            dispatch(setIsLoadingPaypal(true));

            dispatch(
                change(
                    TICKETS_PAGE_FORM_NAME,
                    'payment.paypalBraintreeNonce',
                    nonce,
                ),
            );

            dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'buyer',
                    getPaypalBuyerData(buyerDetails),
                ),
            );

            return dispatch(submit(TICKETS_PAGE_FORM_NAME));
        }

        return dispatch(submit(CHECKOUT_FORM_NAME));
    };

export const onPaypalPaymentMethodSelected =
    (dispatch: Dispatch<CheckoutState>) => () => {
        return dispatch(getAndSetBraintreeToken());
    };

export const onPaypalError =
    (error: Error) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const LOCALIZED_ERROR_MESSAGE_BY_RAW_ERROR_MESSAGE: Record<
            string,
            GenericLazyString
        > = {
            'Can not open popup window - blocked': gettext(
                'PayPal popup could not be opened. Please, enable popups to pay with PayPal.',
            ),
        };
        const DEFAULT_LOCALIZED_ERROR_MESSAGE = gettext(
            'There was an error connecting to PayPal.',
        );
        const rawErrorMessage = error?.message;

        const isTSPage = isTicketSelectionFullSizePage(getState());

        const localizedErrorMessage =
            (rawErrorMessage &&
                LOCALIZED_ERROR_MESSAGE_BY_RAW_ERROR_MESSAGE[
                    rawErrorMessage
                ]) ||
            DEFAULT_LOCALIZED_ERROR_MESSAGE;

        if (isTSPage) {
            dispatch(
                stopSubmit(TICKETS_PAGE_FORM_NAME, {
                    _error: localizedErrorMessage,
                }),
            );

            dispatch(setSubmitFailed(TICKETS_PAGE_FORM_NAME));
        } else {
            dispatch(
                stopSubmit(CHECKOUT_FORM_NAME, {
                    _error: localizedErrorMessage,
                }),
            );
        }
    };
